import { Feature, FeatureStatus } from '@otto-ec/tracking-bct';
import { BaseComponent2 } from '../../_common/client/base-component2';
import { BannerStyle, Fold, Topic } from '../types';
import { ArticleApi } from './api/article-api';
import { guid } from '@otto-ec/global-resources/misc';
import { EventCallback } from '@otto-ec/global-resources/event-q-bus';

type OdinLoadEvent = {
    articleNumber: string;
    slotIdentifier: string;
};

type OdinClickEvent = {
    articleNumber: string;
    slotIdentifier: string;
};

const trackingNameTable = {
    [BannerStyle.MobileContentAd]: {
        [Fold.Below]: 'SDA-Banner',
        [Fold.Above]: 'SDA-Header',
    },
    [BannerStyle.MediumRect]: {
        [Fold.Below]: 'SDA-Rectangle',
        [Fold.Above]: 'SDA-Rectangle',
    },
};

export class SdaComponent extends BaseComponent2 {
    public rootSelector = '.js_pdp_sda__slot';

    constructor(private readonly callbacks = new Map<Topic, EventCallback | null>()) {
        super();
    }

    public async init(): Promise<void> {
        this.clearCallbacks();
        this.callbacks.set(Topic.Impression, o_global.eventQBus.on(Topic.Impression, this.onLoad.bind(this)));
        this.callbacks.set(Topic.Clicked, o_global.eventQBus.on(Topic.Clicked, this.onClick.bind(this)));
    }

    private clearCallbacks() {
        if (this.callbacks.size > 0) {
            this.callbacks.forEach((callback, topic) => callback && o_global.eventQBus.off(topic, callback));
        }
    }

    public async onReloadComponentEvent(): Promise<void> {
        o_global.eventQBus.emitRetain('as.odin.sda.initialize', {
            slotIdentifier: this.getSdaSlotId(),
            isVariationChange: !!o_product?.state?.isVariationChanged,
        });
    }

    private async onLoad(event: OdinLoadEvent): Promise<void> {
        if (event.slotIdentifier !== this.getSdaSlotId()) {
            return;
        }
        try {
            const variationId = await ArticleApi.getVariationId(event.articleNumber);
            const viewTrackingData = this.createViewTrackingData(variationId);
            this.trackViewFeatures(viewTrackingData);
        } catch (error) {
            console.error('Failed to fetch variationId for SDA component', error);
        }
    }

    private async onClick(event: OdinClickEvent): Promise<void> {
        if (event.slotIdentifier !== this.getSdaSlotId()) {
            return;
        }

        const pdpVariationId = this.getRootElement().dataset.variationId!;
        try {
            const sdaVariationId = await ArticleApi.getVariationId(event.articleNumber);
            o_global.eventQBus.emit('tracking.bct.submitMoveAction', ...this.createClickTrackingData(pdpVariationId, sdaVariationId));
        } catch (error) {
            console.error('Failed to fetch variationId for SDA component', error);
        }
    }

    private dispatchSlotId(): [Fold, BannerStyle] {
        const slotId = this.getSdaSlotId() ?? '';

        const regex = new RegExp(/([a-zA-Z]+)_pdp_(btf|atf)_([a-zA-Z-]+)/);
        const match = regex.exec(slotId);
        if (!match || match.length < 4) {
            return [Fold.Below, BannerStyle.MobileContentAd];
        }

        const fold = match[2] === 'atf' ? Fold.Above : Fold.Below;
        const style = match[3] === 'medium-rectangle' ? BannerStyle.MediumRect : BannerStyle.MobileContentAd;
        return [fold, style];
    }

    private getSdaSlotId() {
        return this.getRootElement().dataset.sdaSlotId;
    }

    private createViewTrackingData(sdaVariationId: string) {
        const parentId = guid();
        const [fold, style] = this.dispatchSlotId();
        const trackingName = trackingNameTable[style][fold];

        return this.createViewTrackingFeatures(sdaVariationId, 'loaded', trackingName, parentId);
    }

    private createClickTrackingData(pdpVariationId: string, sdaVariationId: string) {
        const parentId = guid();
        const [fold, style] = this.dispatchSlotId();
        const trackingName = trackingNameTable[style][fold];

        return [
            {
                product_VariationId: pdpVariationId,
            },
            {
                name: 'click',
                features: this.createViewTrackingFeatures(sdaVariationId, 'clicked', trackingName, parentId),
            },
        ];
    }

    private createViewTrackingFeatures(
        sdaVariationId: string,
        trackingType: FeatureStatus,
        trackingName: string,
        parentId: string
    ): Feature[] {
        return [
            {
                id: parentId,
                name: trackingName,
                status: trackingType,
                position: 99,
                labels: {
                    promo_Feature: ['true'],
                },
            },
            {
                id: guid(),
                name: `${trackingName}-Product`,
                status: trackingType,
                position: 99,
                parentId: parentId,
                variationId: sdaVariationId,
                labels: {},
            },
        ];
    }
}
